import React, { useCallback, useRef, useEffect, useState } from 'react';
import TextBlockWithQuillRenderer from './TextBlockWithQuillRenderer';
import BlockHighlight from './BlockHighlight';
// TODO : Remove providers from parent component to prevent unnecessary re-renders
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS, TEMPLATE_TYPE } from '../../context/complexArtboardContext';
import { constructQuillJSON, constructAutoSaveJSON, convertContractToReact } from '../../utils/TextBlockHelper';
import PageElementsProvider from '../../context/pageElementsContext';
import { TEXT_BLOCK } from '../../utils/complexDocumentConstants';
import { dummyCallback } from '../../utils/complexDocumentConstants';
import { useDefaultStyleContext } from '../../context/complexDefaultStyleContext';

const TextBlockWrapper = props => {
  const { itemUUID, parentUUID, pageId, editable, scaling, adjustScroll, getElementStyles, onClickBlockSelection= dummyCallback, isInteractiveDesign = false } = props;
  const overflowContainerRef = useRef(null);
  const fontChangeRef = useRef(null);
  const [textEditable, setTextEditable] = useState(false);
  const customSelector = useCallback(store => store[itemUUID], []);
  const [textBlock, methods] = useDocumentContext(customSelector, `TextBlock-${itemUUID}`);

  const getDefaultStyleSelector = useCallback(store => ({fontFamily: store.fontFamily, fontSize:store.fontSize}), []);
  const [documentStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const documentStyleRef = useRef(documentStyle);

  const previewModeSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [pageOverflowEnabled] = useArtboardContext(previewModeSelector,  `TextBlock-${itemUUID}`);

  const artboardContextSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK] === itemUUID, []);
  const [currentBlock, updateArtboard] = useArtboardContext(
    artboardContextSelector,
    `TextBlock-${itemUUID}`,
  );
  
  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(
    customVarSelector,
    `TextBlock-${itemUUID}`,
  );

  const selectedBlockSelector = useCallback(store => store[CONTEXT_KEYS.SELECTED_BLOCKS], []);
  const [selectedBlocks] = useArtboardContext(selectedBlockSelector, `TextBlock-${itemUUID}`);

  const currentDesignSelector = useCallback((store) => [TEMPLATE_TYPE.DOCUMENT, TEMPLATE_TYPE.FLOW].includes(store[CONTEXT_KEYS.CURRENT_DESIGN]), []);
  const [isDocumentOrFlowDesign] = useArtboardContext(currentDesignSelector, 'CreativeBlockDesign');

  useEffect(() => {
    const handleFontStyleUpdate = e => {
      if(!editable || textBlock.style.fontFamily || textBlock.style.fontSize) return;
      const { action, newFontFamilyOrSize, styleType } = e.detail;
      const eventData =
        action === 'update'
          ? {
              fontStyleData: newFontFamilyOrSize,
              styleType,
              action,
            }
          : { action };
      if (overflowContainerRef?.current) {
        fontChangeRef.current = true;
        documentStyleRef.current = Object.assign({}, documentStyleRef.current,{[styleType]:newFontFamilyOrSize});
        setTextEditable(true);
        setTimeout(() => {
          overflowContainerRef.current.dispatchEvent(
            new CustomEvent('updateFontFamilySizeEvent', { detail: eventData }),
          );
        }, 500);
      }
    };
    documentStyleRef.current = documentStyle;
    document.addEventListener('textElementFontStyleUpdate', handleFontStyleUpdate);
    return () => {
      document.removeEventListener('textElementFontStyleUpdate', handleFontStyleUpdate);
    };
  }, []);

  const saveBlock = useCallback((contents, undoDiff, redoDiff, cb, ref, op = undefined, newGlobalVariables = []) => {
    if(fontChangeRef.current){
      fontChangeRef.current=false;
      cb();
      return;
    };
    const newContents = constructAutoSaveJSON(contents, documentStyle?.fontFamily, documentStyle?.fontSize, newGlobalVariables);
    methods.updateBlock('text-contents', itemUUID, {
      contents: newContents,
      type: 'block',
      flowUUID: parentUUID,
      isInteractiveDesign: isInteractiveDesign,
      historyChange: {
        undo: undoDiff,
        redo: redoDiff,
        op: op
      }
    }, true, newGlobalVariables);
    cb();
  }, [parentUUID, documentStyle, isInteractiveDesign]);

  /*const updateOverflowContents = useCallback((contents, overflowBlock, overflowPageId) => {
    const newContents = constructAutoSaveJSON(contents, documentStyle?.fontFamily, documentStyle?.fontSize, globalVariables);
    methods.setPage('preview-block', {
      overflowBlock: Object.assign({}, overflowBlock, {
        contents: newContents
      }),
      itemUUID,
      overflowPageId,
      flowUUID: parentUUID
    }, false)
  }, [parentUUID]);*/

  const updateOverflowContents = useCallback((details, overflowBlock, overflowPageId) => {
    methods.setPage('preview-block', {
      overflowBlock,
      itemUUID,
      overflowPageId,
      flowUUID: parentUUID,
      ...details
    }, false)
  }, [parentUUID]);

  const removeTextFromParentPage = useCallback((overflowPageId) => {
    methods.removeTextFromParentPage(parentUUID, pageId, itemUUID, overflowPageId)
  }, [parentUUID, pageId, itemUUID]);

  const updateSidebar = useCallback(() => {
    if (!editable) return;
    if (currentBlock) return;
    updateArtboard({
      [CONTEXT_KEYS.CURRENT_BLOCK]: itemUUID,
      [CONTEXT_KEYS.CURRENT_BLOCK_TYPE] : TEXT_BLOCK,
      [CONTEXT_KEYS.CURRENT_PAGE]: parentUUID,
      [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false,
      [CONTEXT_KEYS.BLOCK_SETTINGS_VISIBLE]: false
    });
  }, [itemUUID, parentUUID, currentBlock, editable]);

  useEffect(() => {
    // TODO : check unmount is called in any scenario while editing other elements
    return () => {
      updateArtboard({ [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]: false, [CONTEXT_KEYS.CURRENT_BLOCK]: '' });
    }
  }, []);

  useEffect(() => {
    if (!currentBlock) {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false, [CONTEXT_KEYS.CURRENT_BLOCK_TYPE]: '' });
    }
    return () => {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    }
  }, [currentBlock]);

  const openSettingsInSidebar = useCallback((open = true) => {
    console.log("openSettingsInSidebar called!! ", open);
    if (open) {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: itemUUID });
    } else {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    }
  }, [itemUUID]);

  const blockSelected = selectedBlocks.includes(itemUUID);

  const convertToQuill = useCallback((editable) => {
    if (editable) {
      // save scroll state of the current page and complex-page-wrapper element and apply
      // the scroll state once quill renders the content to avoid jumping of the cursor
      // and viewport when invoking the quill to render the content.
      const parentLayout = document.getElementById(`vertical-layout-${pageId}`);
      const complexPageWrapperEl = document.getElementById('complex-page-wrapper');
      const scrollElTopOld = complexPageWrapperEl.scrollTop;
      const currentScrollTop = parentLayout.scrollTop;
      setTextEditable(editable);
      return () => {
        parentLayout.scrollTop = currentScrollTop;
        complexPageWrapperEl.scrollTop = scrollElTopOld;
      };
    }
    return;
  }, [pageId]);

  return (

    <PageElementsProvider>
        <TextBlockWithQuillRenderer         
          itemUUID={itemUUID}
          selected={currentBlock}
          defaultContent={constructQuillJSON(textBlock?.contents, documentStyleRef.current?.fontFamily, documentStyleRef.current?.fontSize, globalVariables, pageOverflowEnabled)}
          saveBlock={saveBlock}
          onContainerSingleClick={editable && isDocumentOrFlowDesign && !currentBlock ? (e) => {
            if (e.ctrlKey || e.metaKey) {
             onClickBlockSelection({itemUUID, parentUUID})
            }
          } : undefined}
          onContainerClick={updateSidebar}
          enableSideBar={openSettingsInSidebar}
          backgroundColor={blockSelected ? '#ACCEF7': textBlock.style.backgroundColor}
          padding={textBlock.style.padding || undefined}
          scaling={scaling}
          isPreview={pageOverflowEnabled}
          pageId={pageId} // TODO : Is this needed?
          adjustScroll={adjustScroll}
          ref={overflowContainerRef}
          updateOverflowContents={pageOverflowEnabled ? updateOverflowContents : undefined}
          removeTextFromParentPage={pageOverflowEnabled ? removeTextFromParentPage : undefined}
          editable={editable}
          onMouseMove={(e) => (editable || isInteractiveDesign) && !currentBlock && getElementStyles && getElementStyles(e, itemUUID)}
          textEditable={textEditable}
          setTextEditable={convertToQuill}
          enableFunctionCall={isInteractiveDesign? false : true}
        >
          {currentBlock && textBlock.style.backgroundColor === undefined && (
             <BlockHighlight pageUUID={parentUUID} />
          )}
          {pageOverflowEnabled || !textEditable ? convertContractToReact(textBlock?.contents, documentStyle?.fontFamily, documentStyle?.fontSize, globalVariables) : null}
        </TextBlockWithQuillRenderer>
    </PageElementsProvider>
  );
};

export default TextBlockWrapper;
